// src/components/SeoHead.tsx
import { useEffect } from 'react';

type Props = {
  title: string;
  description: string;
  url: string; // absolute canonical
  image?: string; // absolute OG/Twitter image
  imageAlt?: string;
  siteName?: string;
  twitterSite?: string;
  twitterCreator?: string;
  ogType?: 'website' | 'article' | 'profile' | 'video.other';
  noindex?: boolean;
};

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  }
}

function upsertLink(rel: string, href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    document.head.appendChild(link);
  } else {
    link.setAttribute('href', href);
  }
}

export default function SeoHead({ title, description, url, image, imageAlt = 'StudioTV cover image', siteName = 'StudioTV', twitterSite = '@ytstudiotv', twitterCreator = '@ytstudiotv', ogType = 'website', noindex = false }: Props) {
  useEffect(() => {
    // Title
    document.title = title;

    // Canonical
    upsertLink('canonical', url);

    // Basic meta
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    // Réinitialisé à chaque route : sans ça, un passage par la 404 laisserait
    // `noindex` en place sur la page suivante lors d'une navigation client.
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex,follow' : 'index,follow' });

    // Open Graph
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: siteName });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    if (image) {
      upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
      upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: imageAlt });
      // Common default size for social cards
      upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
      upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
      upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
      upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: imageAlt });
    }

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:site"]', { name: 'twitter:site', content: twitterSite });
    upsertMeta('meta[name="twitter:creator"]', { name: 'twitter:creator', content: twitterCreator });
  }, [title, description, url, image, imageAlt, siteName, twitterSite, twitterCreator, ogType, noindex]);

  return null;
}
